import {
  Accordion,
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Group,
  List,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconArrowRight,
  IconBolt,
  IconBuildingSkyscraper,
  IconChartBar,
  IconCheck,
  IconDatabase,
  IconGift,
  IconRocket,
  IconServerOff,
  IconSparkles,
  IconTrendingUp,
  IconWorld,
  IconX,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { signupHref } from "../lib/hosts.ts";
import { useSession } from "../session/SessionProvider.tsx";
import classes from "./Landing.module.css";

export function Pricing() {
  const { signedIn } = useSession();

  const startCta = (
    <Button component="a" href={signupHref()} size="md" rightSection={<IconArrowRight size={18} />}>
      {signedIn ? "Create an app" : "Start free"}
    </Button>
  );

  return (
    <Box className={classes.page}>
      {/* ── Hero ───────────────────────────────────────────── */}
      <Box className={classes.hero}>
        <div className={classes.heroGlow} />
        <Stack gap="lg" className={classes.heroContent} maw={720} mx="auto" ta="center">
          <Group justify="center">
            <Badge variant="light" size="lg" leftSection={<IconGift size={13} />}>
              Pricing
            </Badge>
          </Group>
          <Title order={1} fz={{ base: 36, sm: 54 }} lh={1.06} fw={800}>
            Free for most projects.{" "}
            <Text span inherit className={classes.accent}>
              Pay only when you grow.
            </Text>
          </Title>
          <Text c="dimmed" fz="lg" mx="auto" maw={600}>
            Being cheap is the whole point — s3p is built to be the least expensive way to run a
            real app. There's no server quietly billing you while nobody's online: start every app
            for free, ship it, and only pay once real traffic shows up, for exactly what you use.
          </Text>
          <Group justify="center" mt="xs">
            {startCta}
          </Group>
          <Text size="xs" c="dimmed">
            No credit card to start · No per-seat fees · No charge for idle apps
          </Text>
        </Stack>
      </Box>

      {/* ── Plans ──────────────────────────────────────────── */}
      <Stack gap="xl" py={56}>
        <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg" style={{ alignItems: "stretch" }}>
          <PlanCard
            icon={<IconRocket size={22} />}
            name="Free"
            price="$0"
            period="forever"
            tagline="Where most projects live — and happily stay."
            features={[
              "5 GB storage + 25 GB bandwidth / month",
              "Up to 1,000 monthly active users",
              "2M reads + 200k writes / month",
              "Unlimited projects, your own subdomain & HTTPS",
              "Full SDK, built-in RBAC & the project console",
              "Per-user security enforced by AWS",
            ]}
            cta={startCta}
            highlight
            highlightLabel="Most projects live here"
          />
          <PlanCard
            icon={<IconTrendingUp size={22} />}
            name="Pay as you go"
            price="Usage-based"
            period="only what you use"
            tagline="Your app took off. Pay for the traffic, not for waiting."
            features={[
              "Everything in Free, then simple metered rates:",
              "Storage — $0.05 / GB-month",
              "Requests — $1 per million",
              "Bandwidth — $0.10 / GB",
              "Always-on server runtime — from $7 / month",
              "Usage alerts, spend caps & email support",
            ]}
            cta={
              <Button
                component="a"
                href={signupHref()}
                size="md"
                variant="default"
                rightSection={<IconArrowRight size={18} />}
              >
                Start free, upgrade later
              </Button>
            }
          />
          <PlanCard
            icon={<IconBuildingSkyscraper size={22} />}
            name="Scale"
            price="Custom"
            period="let's talk"
            tagline="Bigger needs: compliance, support, and custom domains."
            features={[
              "Everything in Pay as you go",
              "Custom domains & multi-region options",
              "SSO, audit, and compliance support",
              "Priority support with an SLA",
              "Volume pricing and committed-use discounts",
              "A human who knows your account",
            ]}
            cta={
              <Button
                component="a"
                href="mailto:hello@s3p.dev?subject=s3p%20Scale%20plan"
                size="md"
                variant="default"
                rightSection={<IconArrowRight size={18} />}
              >
                Talk to us
              </Button>
            }
          />
        </SimpleGrid>
        <Text size="xs" c="dimmed" ta="center">
          You'll always see exact metered rates before anything is billed — and nothing is billed
          until you opt into Pay as you go.
        </Text>
      </Stack>

      {/* ── Why free is sustainable ────────────────────────── */}
      <Section
        eyebrow="Why we can do this"
        title="Free isn't a loss-leader — it's the architecture."
        lead="Most platforms run a server for every app, so an idle project still costs them money — and they pass that on. s3p doesn't."
      >
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="lg">
          <WhyCard icon={<IconServerOff size={20} />} title="No server per project">
            Your app runs in your users' browsers. A quiet project has nothing running, so it costs
            almost nothing to keep alive — which is why we can keep it free.
          </WhyCard>
          <WhyCard icon={<IconDatabase size={20} />} title="You pay storage, not standby">
            Costs scale with what you actually store and serve. No traffic, no bill. Lots of
            traffic, a bill that tracks it fairly.
          </WhyCard>
          <WhyCard icon={<IconBolt size={20} />} title="Growth pays for growth">
            The apps that get big are the ones that pay — so the free tier stays genuinely free for
            everyone still finding their feet.
          </WhyCard>
        </SimpleGrid>
      </Section>

      {/* ── What counts as usage ───────────────────────────── */}
      <Section
        eyebrow="When you do pay"
        title="What counts as usage — in plain English."
        lead="Three simple dimensions, all metered. Most projects never get close to the free allowance on any of them."
      >
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="lg">
          <UsageCard icon={<IconDatabase size={20} />} title="Storage" rate="$0.05 / GB-month">
            How much data your app keeps — records, files, uploads. Free up to 5 GB; after that,
            five cents per GB per month.
          </UsageCard>
          <UsageCard icon={<IconChartBar size={20} />} title="Requests" rate="$1 / million">
            Reads and writes your users make. Free up to 2M reads + 200k writes a month, then a
            dollar per million.
          </UsageCard>
          <UsageCard icon={<IconWorld size={20} />} title="Bandwidth" rate="$0.10 / GB">
            Data served to your users. Free up to 25 GB a month; cached at the edge, so it stays low
            for most apps.
          </UsageCard>
        </SimpleGrid>
        <Text size="xs" c="dimmed" mt="xs">
          Example rates, shown to make planning tangible. Your real bill tracks actual usage, and
          you'll always see exact pricing before you opt into Pay as you go.
        </Text>
      </Section>

      {/* ── No surprise-bill traps ─────────────────────────── */}
      <Section
        eyebrow="No fine print games"
        title="The traps we refuse to set."
        lead="Pricing should never punish you for succeeding — or for sitting still."
      >
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xl">
          <Card withBorder radius="lg" padding="xl" className={classes.lift}>
            <Text fw={700} mb="md" tt="uppercase" fz="xs" lts={1} className={classes.accent}>
              What you'll never see
            </Text>
            <List spacing="sm" icon={<IconX size={16} color="var(--mantine-color-red-5)" />}>
              <List.Item>A charge for an app that's idle</List.Item>
              <List.Item>Per-seat pricing that taxes your team's growth</List.Item>
              <List.Item>A monthly minimum just to keep a project alive</List.Item>
              <List.Item>A bill that lands before you ever saw the rate</List.Item>
            </List>
          </Card>
          <Card withBorder radius="lg" padding="xl" className={classes.lift}>
            <Text fw={700} mb="md" tt="uppercase" fz="xs" lts={1} c="green.5">
              What you'll always get
            </Text>
            <List
              spacing="sm"
              icon={
                <ThemeIcon size={20} radius="xl" variant="light">
                  <IconCheck size={13} />
                </ThemeIcon>
              }
            >
              <List.Item>A real free tier you can ship on</List.Item>
              <List.Item>Usage alerts before you cross a threshold</List.Item>
              <List.Item>A spend cap you control</List.Item>
              <List.Item>Rates you can read in one line</List.Item>
            </List>
          </Card>
        </SimpleGrid>
      </Section>

      {/* ── FAQ ────────────────────────────────────────────── */}
      <Section eyebrow="Pricing, answered" title="The questions you're about to ask.">
        <Accordion variant="separated" radius="md" defaultValue="free">
          <FaqItem value="free" q="Is the free tier actually free — or free-trial free?">
            Actually free. No clock, no credit card. Most projects fit inside the free allowance and
            simply stay there. You only ever pay if you choose to grow past it.
          </FaqItem>
          <FaqItem value="when" q="When do I start paying?">
            Only when you opt into Pay as you go and your usage climbs past the free allowance.
            Until then there's nothing to pay and nothing to cancel.
          </FaqItem>
          <FaqItem value="surprise" q="Could I get a surprise bill?">
            We design against it. You'll get alerts as you approach a threshold, you can set a hard
            spend cap, and you'll always have seen the exact rate before anything is metered.
          </FaqItem>
          <FaqItem value="idle" q="Do I pay to keep an app I'm not using?">
            No. An idle app has nothing running, so it costs nothing. Park a side project for a year
            and come back to a $0 balance.
          </FaqItem>
          <FaqItem value="stop" q="What if I stop paying?">
            Your project isn't deleted out from under you. Paid features (like the always-on server
            runtime and higher limits) pause, and your app drops back to free-tier behavior.
          </FaqItem>
          <FaqItem value="leave" q="Am I locked in once I'm paying?">
            No. It's standard S3, Cognito, and IAM under the hood, and the SDK is open — your code
            and your data can move with you. Pricing earns your stay; it doesn't trap it.
          </FaqItem>
        </Accordion>
      </Section>

      {/* ── CTA ────────────────────────────────────────────── */}
      <Box className={classes.ctaBand} p={{ base: "xl", sm: 48 }} my={64}>
        <Stack align="center" ta="center" gap="md">
          <ThemeIcon size={52} radius="xl" variant="light">
            <IconSparkles size={28} />
          </ThemeIcon>
          <Title order={2} fz={{ base: 28, sm: 36 }} maw={620}>
            Start for free. Pay the day it pays off.
          </Title>
          <Text c="dimmed" maw={520}>
            Name your app and you're building in minutes — on a tier that costs nothing until you
            actually need more.
          </Text>
          <Group mt="sm">
            {startCta}
            <Button component={Link} to="/security" size="md" variant="default">
              How it works
            </Button>
          </Group>
        </Stack>
      </Box>
    </Box>
  );
}

/* ── helpers (match Landing/Security style) ───────────────── */

function Section({
  eyebrow,
  title,
  lead,
  children,
}: {
  eyebrow: string;
  title: React.ReactNode;
  lead?: string;
  children?: React.ReactNode;
}) {
  return (
    <Stack gap="xl" py={56}>
      <Stack gap="sm" maw={760}>
        <Text tt="uppercase" fz="xs" fw={700} lts={2} className={classes.accent}>
          {eyebrow}
        </Text>
        <Title order={2} fz={{ base: 26, sm: 36 }} lh={1.12}>
          {title}
        </Title>
        {lead && (
          <Text c="dimmed" fz="lg">
            {lead}
          </Text>
        )}
      </Stack>
      {children}
    </Stack>
  );
}

function PlanCard({
  icon,
  name,
  price,
  period,
  tagline,
  features,
  cta,
  highlight,
  highlightLabel,
}: {
  icon: React.ReactNode;
  name: string;
  price: string;
  period: string;
  tagline: string;
  features: string[];
  cta: React.ReactNode;
  highlight?: boolean;
  highlightLabel?: string;
}) {
  return (
    <Card
      withBorder
      radius="lg"
      padding="xl"
      h="100%"
      className={classes.lift}
      style={
        highlight ? { borderColor: "var(--mantine-color-brand-5)", borderWidth: 2 } : undefined
      }
    >
      <Stack gap="md" h="100%">
        <Group justify="space-between" wrap="nowrap">
          <ThemeIcon variant="light" size={44} radius="md">
            {icon}
          </ThemeIcon>
          {highlight && highlightLabel && (
            <Badge variant="filled" size="sm">
              {highlightLabel}
            </Badge>
          )}
        </Group>
        <div>
          <Text fw={700} fz="xl">
            {name}
          </Text>
          <Text c="dimmed" size="sm">
            {tagline}
          </Text>
        </div>
        <Group gap={6} align="baseline">
          <Text fw={800} fz={32} lh={1}>
            {price}
          </Text>
          <Text c="dimmed" size="sm">
            {period}
          </Text>
        </Group>
        {cta}
        <List
          spacing="xs"
          mt="xs"
          icon={
            <ThemeIcon size={18} radius="xl" variant="light">
              <IconCheck size={11} />
            </ThemeIcon>
          }
        >
          {features.map((f) => (
            <List.Item key={f}>
              <Text size="sm">{f}</Text>
            </List.Item>
          ))}
        </List>
      </Stack>
    </Card>
  );
}

function WhyCard({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card withBorder radius="lg" padding="xl" h="100%" className={classes.lift}>
      <ThemeIcon variant="light" size="lg" radius="md" mb="sm">
        {icon}
      </ThemeIcon>
      <Text fw={700} mb={4}>
        {title}
      </Text>
      <Text c="dimmed" size="sm">
        {children}
      </Text>
    </Card>
  );
}

function UsageCard({
  icon,
  title,
  rate,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  rate: string;
  children: React.ReactNode;
}) {
  return (
    <Card withBorder radius="lg" padding="lg" h="100%" className={classes.lift}>
      <Group justify="space-between" mb="sm" wrap="nowrap">
        <Group gap="sm" wrap="nowrap">
          <ThemeIcon variant="light" size="lg" radius="md">
            {icon}
          </ThemeIcon>
          <Text fw={700}>{title}</Text>
        </Group>
        <Badge variant="light" size="sm">
          {rate}
        </Badge>
      </Group>
      <Text c="dimmed" size="sm">
        {children}
      </Text>
    </Card>
  );
}

function FaqItem({ value, q, children }: { value: string; q: string; children: React.ReactNode }) {
  return (
    <Accordion.Item value={value}>
      <Accordion.Control>
        <Text fw={600}>{q}</Text>
      </Accordion.Control>
      <Accordion.Panel>
        <Text c="dimmed">{children}</Text>
      </Accordion.Panel>
    </Accordion.Item>
  );
}
